/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import swal from "sweetalert";
import jsPDF from "jspdf";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Icons
import VisibilityIcon from "@mui/icons-material/Visibility";
import PersonIcon from "@mui/icons-material/Person";
import SearchIcon from "@mui/icons-material/Search";
import PeopleIcon from "@mui/icons-material/People";

import { logoutUser } from "actions/AuthAction";
import { useNavigate } from "react-router-dom";
import { API_ESA_OFFICE, API_ESA_OFFICE_UPLOAD } from "utils";

// Helper untuk format datetime
function formatDateTime(dateStr) {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  if (isNaN(date)) return "-";
  return date.toLocaleString("id-ID", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function EventYangDiikuti() {
  const [detailEventsList, setDetailEventsList] = useState([]);
  const [filteredEventsList, setFilteredEventsList] = useState([]);
  const [uid_RSS, setUid_RSS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tahun, setTahun] = useState(new Date().getFullYear());
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // State untuk dialog view notulen/foto
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [viewingDetailEvent, setViewingDetailEvent] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  // State untuk dialog daftar peserta
  const [openPesertaDialog, setOpenPesertaDialog] = useState(false);
  const [daftarPeserta, setDaftarPeserta] = useState([]);
  const [loadingPeserta, setLoadingPeserta] = useState(false);
  const [selectedDetailEventId, setSelectedDetailEventId] = useState(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setUid_RSS(user.uid_RSS);
    } else {
      logoutUser(navigate);
    }
  }, []);

  useEffect(() => {
    const fetchDetailEvents = async () => {
      if (!uid_RSS) {
        console.log("uid_RSS belum tersedia, menunggu...");
        return;
      }

      console.log("Fetching attended events dengan uid_RSS:", uid_RSS);
      setLoading(true);
      try {
        let url = `${API_ESA_OFFICE}events/attended?uid_RSS=${uid_RSS}`;
        if (tahun) {
          url += `&tahun=${tahun}`;
        }
        console.log("Request URL:", url);
        const response = await axios.get(url);
        console.log("Response data:", response.data);
        setDetailEventsList(response.data || []);
      } catch (error) {
        console.error("Error fetching attended events:", error);
        console.error("Error response:", error.response?.data);
        console.error("Error status:", error.response?.status);
        swal({
          title: "Error",
          text:
            error.response?.data?.message ||
            "Gagal mengambil data event yang diikuti",
          icon: "error",
        });
        setDetailEventsList([]);
      } finally {
        setLoading(false);
      }
    };

    console.log("useEffect triggered, uid_RSS:", uid_RSS, "tahun:", tahun);
    if (uid_RSS) {
      fetchDetailEvents();
    }
  }, [uid_RSS, tahun]);

  // Filter berdasarkan search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredEventsList(detailEventsList);
      return;
    }

    const query = searchQuery.toLowerCase().trim();
    const filtered = detailEventsList.filter((event) => {
      const kegiatan = (event.kegiatan || "").toLowerCase();
      const detailKegiatan = (event.detail_kegiatan || "").toLowerCase();
      const tempat = (event.tempat || "").toLowerCase();

      return (
        kegiatan.includes(query) ||
        detailKegiatan.includes(query) ||
        tempat.includes(query)
      );
    });

    setFilteredEventsList(filtered);
  }, [searchQuery, detailEventsList]);

  const generatePDF = async (detailEvent) => {
    setPdfLoading(true);
    // Fetch data peserta
    let pesertaList = [];
    try {
      const pesertaResponse = await axios.get(
        `${API_ESA_OFFICE}attendance/${detailEvent.id}`
      );
      pesertaList = pesertaResponse.data || [];
    } catch (error) {
      console.error("Error fetching peserta:", error);
    }
    const doc = new jsPDF();
    let yPosition = 20;

    // Helper function untuk render HTML dengan mempertahankan formatting
    const renderHtmlContent = (html, x, y, maxWidth) => {
      if (!html) return y;

      let currentY = y;
      const lineHeight = 5;
      const paragraphSpacing = 3;

      // Set default font normal
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);

      // Parse HTML
      const tmp = document.createElement("DIV");
      tmp.innerHTML = html;

      // Track current font state
      let currentFontStyle = "normal"; // "normal", "bold", "italic"

      // Process nodes recursively
      const processNode = (node, indent = 0, parentFontStyle = "normal") => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent.trim();
          if (text) {
            // Check if need new page
            if (currentY > 270) {
              doc.addPage();
              currentY = 20;
            }

            // Get computed style untuk text alignment
            const parent = node.parentElement;
            const textAlign =
              parent?.style?.textAlign ||
              window.getComputedStyle(parent || document.body).textAlign ||
              "left";

            // Set font sesuai parent font style
            if (parentFontStyle === "bold") {
              doc.setFont("helvetica", "bold");
            } else if (parentFontStyle === "italic") {
              doc.setFont("helvetica", "italic");
            } else {
              doc.setFont("helvetica", "normal");
            }

            // Split text to fit width
            const lines = doc.splitTextToSize(text, maxWidth - indent * 5);

            // Render each line dengan alignment
            lines.forEach((line) => {
              if (currentY > 270) {
                doc.addPage();
                currentY = 20;
              }

              let xPos = x + indent * 5;
              if (textAlign === "center") {
                xPos = 105; // Center of page
                doc.text(line, xPos, currentY, { align: "center" });
              } else if (textAlign === "right") {
                xPos = x + maxWidth;
                doc.text(line, xPos, currentY, { align: "right" });
              } else {
                doc.text(line, xPos, currentY);
              }
              currentY += lineHeight;
            });
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const tagName = node.tagName?.toLowerCase();

          // Handle different HTML elements
          if (tagName === "p" || tagName === "div") {
            // Add spacing before paragraph (except first)
            if (currentY !== y && node.previousSibling) {
              currentY += paragraphSpacing;
            }

            // Process children dengan font style dari parent
            Array.from(node.childNodes).forEach((child) => {
              processNode(child, indent, parentFontStyle);
            });

            // Add spacing after paragraph
            currentY += paragraphSpacing;
          } else if (tagName === "br") {
            currentY += lineHeight;
          } else if (tagName === "strong" || tagName === "b") {
            // Bold text - pass "bold" to children
            Array.from(node.childNodes).forEach((child) => {
              processNode(child, indent, "bold");
            });
          } else if (tagName === "em" || tagName === "i") {
            // Italic text - pass "italic" to children
            Array.from(node.childNodes).forEach((child) => {
              processNode(child, indent, "italic");
            });
          } else if (tagName === "ul" || tagName === "ol") {
            // List
            Array.from(node.children).forEach((child, index) => {
              if (currentY > 270) {
                doc.addPage();
                currentY = 20;
              }

              // Set font normal untuk bullet
              doc.setFont("helvetica", "normal");
              const bullet = tagName === "ul" ? "•" : `${index + 1}.`;
              doc.text(bullet, x + indent * 5, currentY);
              processNode(child, indent + 1, parentFontStyle);
            });
          } else if (tagName === "li") {
            // List item
            Array.from(node.childNodes).forEach((child) => {
              processNode(child, indent, parentFontStyle);
            });
          } else if (tagName === "h1" || tagName === "h2" || tagName === "h3") {
            // Headings - always bold
            if (currentY !== y && node.previousSibling) {
              currentY += paragraphSpacing * 2;
            }
            const fontSize = tagName === "h1" ? 16 : tagName === "h2" ? 14 : 12;
            doc.setFontSize(fontSize);
            doc.setFont("helvetica", "bold");
            Array.from(node.childNodes).forEach((child) => {
              processNode(child, indent, "bold");
            });
            doc.setFontSize(11);
            doc.setFont("helvetica", "normal");
            currentY += paragraphSpacing;
          } else {
            // Process other elements - maintain parent font style
            Array.from(node.childNodes).forEach((child) => {
              processNode(child, indent, parentFontStyle);
            });
          }
        }
      };

      // Process all nodes dengan font normal sebagai default
      Array.from(tmp.childNodes).forEach((child) => {
        processNode(child, 0, "normal");
      });

      // Reset font ke normal setelah selesai
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);

      return currentY;
    };

    // Header
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Laporan Event", 105, yPosition, { align: "center" });
    yPosition += 10;

    // Informasi Event
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(`Nama Event: ${detailEvent.kegiatan || "-"}`, 14, yPosition);
    yPosition += 7;
    doc.text(
      `Detail Kegiatan: ${detailEvent.detail_kegiatan || "-"}`,
      14,
      yPosition
    );
    yPosition += 7;
    doc.text(`Tempat: ${detailEvent.tempat || "-"}`, 14, yPosition);
    yPosition += 7;

    const waktuMulai = detailEvent.waktu_mulai
      ? new Date(detailEvent.waktu_mulai).toLocaleString("id-ID")
      : "-";
    const waktuFinish = detailEvent.waktu_finish
      ? new Date(detailEvent.waktu_finish).toLocaleString("id-ID")
      : "-";
    doc.text(`Waktu Mulai: ${waktuMulai}`, 14, yPosition);
    yPosition += 7;
    doc.text(`Waktu Selesai: ${waktuFinish}`, 14, yPosition);
    yPosition += 10;

    // Notulen
    if (detailEvent.notulen && detailEvent.notulen.trim() !== "") {
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Notulen", 14, yPosition);
      yPosition += 7;

      // Check if need new page
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }

      // Render HTML content dengan mempertahankan formatting
      yPosition = renderHtmlContent(detailEvent.notulen, 14, yPosition, 180);
    }

    // Foto
    if (
      detailEvent.foto &&
      Array.isArray(detailEvent.foto) &&
      detailEvent.foto.length > 0
    ) {
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(`Foto Kegiatan (${detailEvent.foto.length})`, 14, yPosition);
      yPosition += 10;

      for (let i = 0; i < detailEvent.foto.length; i++) {
        const fotoUrl = detailEvent.foto[i];
        const fullUrl = fotoUrl.startsWith("/")
          ? `${API_ESA_OFFICE_UPLOAD}${fotoUrl}`
          : `${API_ESA_OFFICE_UPLOAD}/${fotoUrl}`;

        try {
          // Load image
          const img = await new Promise((resolve, reject) => {
            const image = new Image();
            image.crossOrigin = "anonymous";
            image.onload = () => resolve(image);
            image.onerror = reject;
            image.src = fullUrl;
          });

          // Calculate image dimensions to fit page width
          const maxWidth = 180;
          const maxHeight = 100;
          let imgWidth = img.width;
          let imgHeight = img.height;
          const ratio = Math.min(maxWidth / imgWidth, maxHeight / imgHeight);
          imgWidth = imgWidth * ratio;
          imgHeight = imgHeight * ratio;

          // Check if need new page
          if (yPosition + imgHeight > 270) {
            doc.addPage();
            yPosition = 20;
          }

          // Add image
          doc.text(`Foto ${i + 1}:`, 14, yPosition);
          yPosition += 5;
          doc.addImage(img, "JPEG", 14, yPosition, imgWidth, imgHeight);
          yPosition += imgHeight + 10;

          // Check if need new page after image
          if (yPosition > 250 && i < detailEvent.foto.length - 1) {
            doc.addPage();
            yPosition = 20;
          }
        } catch (error) {
          console.error("Error loading image:", error);
          doc.text(`Foto ${i + 1}: Gagal memuat gambar`, 14, yPosition);
          yPosition += 7;
        }
      }
    }

    // Daftar Peserta
    if (pesertaList.length > 0) {
      // Check if need new page
      if (yPosition > 220) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(`Daftar Peserta (${pesertaList.length} orang)`, 14, yPosition);
      yPosition += 10;

      // Table header
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      const tableStartY = yPosition;
      doc.text("No", 14, yPosition);
      doc.text("Nama", 25, yPosition);
      doc.text("Instansi", 90, yPosition);
      doc.text("Waktu", 150, yPosition);
      yPosition += 6;

      // Table rows
      doc.setFont("helvetica", "normal");
      pesertaList.forEach((peserta, index) => {
        // Check if need new page
        if (yPosition > 270) {
          doc.addPage();
          yPosition = 20;
          // Redraw header
          doc.setFont("helvetica", "bold");
          doc.text("No", 14, yPosition);
          doc.text("Nama", 25, yPosition);
          doc.text("Instansi", 90, yPosition);
          doc.text("Waktu", 150, yPosition);
          yPosition += 6;
          doc.setFont("helvetica", "normal");
        }

        const nama = peserta.nama || "-";
        const instansi = peserta.instansi || "-";
        const waktu = peserta.waktu
          ? new Date(peserta.waktu).toLocaleString("id-ID", {
              day: "numeric",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })
          : "-";

        const currentY = yPosition;
        doc.text(`${index + 1}`, 14, currentY);
        // Split long text
        const namaLines = doc.splitTextToSize(nama, 60);
        const instansiLines = doc.splitTextToSize(instansi, 55);
        const waktuLines = doc.splitTextToSize(waktu, 35);

        const maxLines = Math.max(
          namaLines.length,
          instansiLines.length,
          waktuLines.length
        );
        const lineHeight = 5;

        for (let i = 0; i < maxLines; i++) {
          const rowY = currentY + i * lineHeight;
          doc.text(namaLines[i] || "", 25, rowY);
          doc.text(instansiLines[i] || "", 90, rowY);
          doc.text(waktuLines[i] || "", 150, rowY);
        }

        yPosition = currentY + maxLines * lineHeight + 2;
      });

      // Draw table border
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.1);
      const tableEndY = yPosition - 2;
      doc.line(14, tableStartY - 4, 14, tableEndY); // Left border
      doc.line(180, tableStartY - 4, 180, tableEndY); // Right border
      doc.line(14, tableStartY - 4, 180, tableStartY - 4); // Top border
      doc.line(14, tableEndY, 180, tableEndY); // Bottom border
      doc.line(23, tableStartY - 4, 23, tableEndY); // No border
      doc.line(88, tableStartY - 4, 88, tableEndY); // Nama border
      doc.line(148, tableStartY - 4, 148, tableEndY); // Instansi border
    }

    // Generate PDF blob
    const pdfBlob = doc.output("blob");
    const url = URL.createObjectURL(pdfBlob);
    setPdfLoading(false);
    return url;
  };

  const handleOpenViewDialog = async (detailEvent) => {
    setViewingDetailEvent(detailEvent);
    setOpenViewDialog(true);
    setPdfLoading(true);

    // Generate PDF
    try {
      const pdfUrl = await generatePDF(detailEvent);
      setPdfBlobUrl(pdfUrl);
    } catch (error) {
      console.error("Error generating PDF:", error);
      swal({
        title: "Error",
        text: "Gagal membuat PDF",
        icon: "error",
      });
      setPdfLoading(false);
    }
  };

  const handleCloseViewDialog = () => {
    // Clean up blob URL
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
    setOpenViewDialog(false);
    setViewingDetailEvent(null);
    setPdfLoading(false);
  };

  const handleOpenPesertaDialog = async (detailEventId, jumlahPeserta) => {
    if (jumlahPeserta === 0) return;

    setSelectedDetailEventId(detailEventId);
    setOpenPesertaDialog(true);
    setLoadingPeserta(true);
    setDaftarPeserta([]);

    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}attendance/${detailEventId}`
      );
      setDaftarPeserta(response.data || []);
    } catch (error) {
      console.error("Error fetching daftar peserta:", error);
      swal({
        title: "Error",
        text: "Gagal mengambil data peserta",
        icon: "error",
      });
      setDaftarPeserta([]);
    } finally {
      setLoadingPeserta(false);
    }
  };

  const handleClosePesertaDialog = () => {
    setOpenPesertaDialog(false);
    setDaftarPeserta([]);
    setSelectedDetailEventId(null);
  };

  const getStatusEventLabel = (status) => {
    switch (status) {
      case "satu_kali":
        return "Satu Kali";
      case "rutin":
        return "Rutin";
      default:
        return status;
    }
  };

  const getStatusEventColor = (status) => {
    switch (status) {
      case "satu_kali":
        return "info";
      case "rutin":
        return "warning";
      default:
        return "default";
    }
  };

  const columns = [
    { Header: "No", accessor: "no", width: "5%" },
    {
      Header: "Nama Event",
      accessor: "kegiatan",
      width: "200px",
      Cell: ({ value }) => (
        <MDBox
          sx={{
            whiteSpace: "normal",
            wordWrap: "break-word",
            width: "200px",
            py: 1,
          }}
        >
          <MDTypography variant="caption" color="text">
            {value || "-"}
          </MDTypography>
        </MDBox>
      ),
    },
    {
      Header: "Status Event",
      accessor: "status_event",
      width: "12%",
      Cell: ({ value }) => (
        <Chip
          label={getStatusEventLabel(value)}
          color={getStatusEventColor(value)}
          size="small"
          variant="filled"
        />
      ),
    },
    {
      Header: "Detail Kegiatan",
      accessor: "detail_kegiatan",
      width: "200px",
      Cell: ({ value }) => (
        <MDBox
          sx={{
            whiteSpace: "normal",
            wordWrap: "break-word",
            width: "200px",
            py: 1,
          }}
        >
          <MDTypography variant="caption" color="text">
            {value || "-"}
          </MDTypography>
        </MDBox>
      ),
    },
    {
      Header: "Waktu Mulai",
      accessor: "waktu_mulai",
      width: "15%",
      Cell: ({ value }) => (
        <MDTypography variant="caption" color="text">
          {formatDateTime(value)}
        </MDTypography>
      ),
    },
    {
      Header: "Waktu Selesai",
      accessor: "waktu_finish",
      width: "15%",
      Cell: ({ value }) => (
        <MDTypography variant="caption" color="text">
          {formatDateTime(value)}
        </MDTypography>
      ),
    },
    {
      Header: "Tempat",
      accessor: "tempat",
      width: "200px",
      Cell: ({ value }) => (
        <MDBox
          sx={{
            whiteSpace: "normal",
            wordWrap: "break-word",
            width: "200px",
            py: 1,
          }}
        >
          <MDTypography variant="caption" color="text">
            {value || "-"}
          </MDTypography>
        </MDBox>
      ),
    },
    {
      Header: "Peserta",
      accessor: "jumlah_peserta",
      width: "10%",
      Cell: ({ row, value }) => {
        const jumlahPeserta = value || 0;
        return (
          <Button
            variant="outlined"
            size="small"
            startIcon={<PeopleIcon />}
            onClick={() =>
              handleOpenPesertaDialog(row.original.id, jumlahPeserta)
            }
            disabled={jumlahPeserta === 0}
            sx={{
              textTransform: "none",
              color: "info.main",
            }}
          >
            Lihat ({jumlahPeserta})
          </Button>
        );
      },
    },
    {
      Header: "Pembuat",
      accessor: "nama_pembuat",
      width: "15%",
      Cell: ({ row }) => (
        <MDBox display="flex" alignItems="center" gap={1}>
          {row.original.uid_RSS_pembuat ? (
            <img
              src={`https://smartdh.rotendaokab.go.id/uploads/${row.original.uid_RSS_pembuat}.jpg`}
              alt={row.original.nama_pembuat || "User"}
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                objectFit: "cover",
              }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          ) : (
            <MDBox
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                backgroundColor: "grey.200",
                color: "grey.500",
              }}
            >
              <PersonIcon sx={{ fontSize: 20 }} />
            </MDBox>
          )}
          <MDTypography variant="caption" color="text">
            {row.original.nama_pembuat || "-"}
          </MDTypography>
        </MDBox>
      ),
    },
    {
      Header: "Aksi",
      accessor: "aksi",
      width: "10%",
      Cell: ({ row }) => {
        const hasLaporan =
          (row.original.notulen && row.original.notulen.trim() !== "") ||
          (row.original.foto &&
            Array.isArray(row.original.foto) &&
            row.original.foto.length > 0);
        return (
          <MDBox display="flex" gap={1}>
            {hasLaporan && (
              <Tooltip title="Lihat Laporan">
                <IconButton
                  color="success"
                  size="small"
                  onClick={() => handleOpenViewDialog(row.original)}
                >
                  <VisibilityIcon />
                </IconButton>
              </Tooltip>
            )}
          </MDBox>
        );
      },
    },
  ];

  const rows = filteredEventsList.map((detailEvent, index) => ({
    id: detailEvent.id,
    no: index + 1,
    kegiatan: detailEvent.kegiatan || "-",
    status_event: detailEvent.status_event || "-",
    detail_kegiatan: detailEvent.detail_kegiatan || "-",
    waktu_mulai: detailEvent.waktu_mulai,
    waktu_finish: detailEvent.waktu_finish,
    tempat: detailEvent.tempat || "-",
    jumlah_peserta: detailEvent.jumlah_peserta || 0,
    nama_pembuat: detailEvent.nama_pembuat || "-",
    id_pembuat: detailEvent.id_pembuat,
    uid_RSS_pembuat: detailEvent.uid_RSS_pembuat,
    notulen: detailEvent.notulen,
    foto: detailEvent.foto,
    nama_dinas: detailEvent.nama_dinas,
    nama_bidang: detailEvent.nama_bidang,
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Event yang Diikuti
                </MDTypography>
              </MDBox>
              <MDBox pt={4} pb={3} px={3}>
                {/* Filter Tahun dan Search */}
                <Grid container spacing={2} mb={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                      options={(() => {
                        const currentYear = new Date().getFullYear();
                        const years = [];
                        // Mulai dari 2025 sampai current tahun
                        for (let i = 2025; i <= currentYear; i++) {
                          years.push({ label: i.toString(), value: i });
                        }
                        // Urutkan dari terbaru ke terlama (DESC)
                        return years.reverse();
                      })()}
                      value={
                        tahun ? { label: tahun.toString(), value: tahun } : null
                      }
                      onChange={(event, newValue) => {
                        if (newValue) {
                          setTahun(newValue.value);
                        }
                      }}
                      renderInput={(params) => (
                        <MDInput
                          {...params}
                          label="Tahun"
                          variant="standard"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <MDInput
                      label="Cari Nama Event, Detail Event, atau Lokasi"
                      variant="standard"
                      fullWidth
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
                {loading ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="200px"
                  >
                    <CircularProgress />
                    <MDTypography variant="body2" color="text" ml={2}>
                      Memuat data...
                    </MDTypography>
                  </MDBox>
                ) : isMobile ? (
                  <MDBox display="flex" flexDirection="column" gap={1}>
                    {filteredEventsList.length === 0 ? (
                      <MDBox
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        py={4}
                      >
                        <MDTypography variant="h6" color="text.secondary">
                          Tidak ada data event yang diikuti
                        </MDTypography>
                        <MDTypography variant="body2" color="text.secondary">
                          Belum ada event yang Anda ikuti
                        </MDTypography>
                      </MDBox>
                    ) : (
                      filteredEventsList.map((detailEvent, index) => {
                        const hasLaporan =
                          (detailEvent.notulen &&
                            detailEvent.notulen.trim() !== "") ||
                          (detailEvent.foto &&
                            Array.isArray(detailEvent.foto) &&
                            detailEvent.foto.length > 0);
                        return (
                          <Card key={detailEvent.id}>
                            <CardContent>
                              <MDBox>
                                <MDTypography variant="h6" fontWeight="bold">
                                  {detailEvent.kegiatan || "-"}
                                </MDTypography>
                                <MDBox
                                  mt={1}
                                  display="flex"
                                  gap={1}
                                  alignItems="center"
                                  flexWrap="wrap"
                                >
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Status:
                                  </MDTypography>
                                  <Chip
                                    label={getStatusEventLabel(
                                      detailEvent.status_event
                                    )}
                                    color={getStatusEventColor(
                                      detailEvent.status_event
                                    )}
                                    size="small"
                                    variant="filled"
                                  />
                                </MDBox>
                                {detailEvent.detail_kegiatan && (
                                  <MDBox mt={1}>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Detail Kegiatan:
                                    </MDTypography>
                                    <MDTypography variant="body2" color="text">
                                      {detailEvent.detail_kegiatan}
                                    </MDTypography>
                                  </MDBox>
                                )}
                                {detailEvent.waktu_mulai && (
                                  <MDBox mt={1}>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Waktu Mulai:
                                    </MDTypography>
                                    <MDTypography variant="body2" color="text">
                                      {formatDateTime(detailEvent.waktu_mulai)}
                                    </MDTypography>
                                  </MDBox>
                                )}
                                {detailEvent.waktu_finish && (
                                  <MDBox mt={1}>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Waktu Selesai:
                                    </MDTypography>
                                    <MDTypography variant="body2" color="text">
                                      {formatDateTime(detailEvent.waktu_finish)}
                                    </MDTypography>
                                  </MDBox>
                                )}
                                {detailEvent.tempat && (
                                  <MDBox mt={1}>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Tempat:
                                    </MDTypography>
                                    <MDTypography variant="body2" color="text">
                                      {detailEvent.tempat}
                                    </MDTypography>
                                  </MDBox>
                                )}
                                {detailEvent.jumlah_peserta !== undefined && (
                                  <MDBox mt={1}>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Peserta:
                                    </MDTypography>
                                    <MDTypography
                                      variant="body2"
                                      color="text"
                                      fontWeight="medium"
                                    >
                                      {detailEvent.jumlah_peserta || 0} orang
                                    </MDTypography>
                                  </MDBox>
                                )}
                                {hasLaporan && (
                                  <MDBox mt={2}>
                                    <Tooltip title="Lihat Laporan">
                                      <IconButton
                                        color="success"
                                        size="small"
                                        onClick={() =>
                                          handleOpenViewDialog(detailEvent)
                                        }
                                      >
                                        <VisibilityIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </MDBox>
                                )}
                              </MDBox>
                            </CardContent>
                          </Card>
                        );
                      })
                    )}
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows }}
                    entriesPerPage={{
                      defaultValue: 10,
                      entries: [5, 10, 15, 20, 25],
                    }}
                    showTotalEntries={true}
                    isSorted={true}
                    noEndBorder
                    loading={loading}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Dialog View Notulen dan Foto - PDF */}
      <Dialog
        open={openViewDialog}
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6">Lihat Laporan Event (PDF)</MDTypography>
            <IconButton onClick={handleCloseViewDialog}>
              <CloseIcon />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            {pdfLoading ? (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
              >
                <CircularProgress />
                <MDTypography variant="body2" color="text" ml={2}>
                  Membuat PDF...
                </MDTypography>
              </MDBox>
            ) : pdfBlobUrl ? (
              <iframe
                src={pdfBlobUrl}
                width="100%"
                height="600px"
                title="PDF Viewer"
                style={{ border: "none" }}
                onError={(e) => {
                  console.error("Error loading PDF:", e);
                  swal({
                    icon: "error",
                    title: "Oops...",
                    text: "Gagal memuat PDF",
                    button: "OK",
                  });
                }}
              />
            ) : null}
          </MDBox>
        </DialogContent>
        <DialogActions>
          {pdfBlobUrl && (
            <Button
              onClick={() => {
                const link = document.createElement("a");
                link.href = pdfBlobUrl;
                link.download = `Laporan_Event_${
                  viewingDetailEvent?.kegiatan || "Event"
                }_${new Date().getTime()}.pdf`;
                link.click();
              }}
              variant="outlined"
              color="primary"
              sx={{ color: "black", borderColor: "black" }}
            >
              Download PDF
            </Button>
          )}
          <Button
            onClick={handleCloseViewDialog}
            variant="contained"
            color="primary"
          >
            Tutup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Daftar Peserta */}
      <Dialog
        open={openPesertaDialog}
        onClose={handleClosePesertaDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6">Daftar Peserta</MDTypography>
            <IconButton onClick={handleClosePesertaDialog}>
              <CloseIcon />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            {loadingPeserta ? (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
              >
                <CircularProgress />
              </MDBox>
            ) : daftarPeserta.length === 0 ? (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
              >
                <MDTypography variant="body2" color="text.secondary">
                  Tidak ada peserta
                </MDTypography>
              </MDBox>
            ) : (
              <MDBox>
                <DataTable
                  table={{
                    columns: [
                      { Header: "No", accessor: "no", width: "5%" },
                      {
                        Header: "Nama",
                        accessor: "nama",
                        width: "25%",
                        Cell: ({ row }) => (
                          <MDBox display="flex" alignItems="center" gap={1}>
                            {row.original.uid_RSS ? (
                              <img
                                src={`https://smartdh.rotendaokab.go.id/uploads/${row.original.uid_RSS}.jpg`}
                                alt={
                                  row.original.nama_user || row.original.nama
                                }
                                style={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: "50%",
                                  objectFit: "cover",
                                }}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            ) : (
                              <MDBox
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: "50%",
                                  backgroundColor: "grey.200",
                                  color: "grey.500",
                                }}
                              >
                                <PersonIcon sx={{ fontSize: 20 }} />
                              </MDBox>
                            )}
                            <MDTypography variant="caption" color="text">
                              {row.original.nama_user ||
                                row.original.nama ||
                                "-"}
                            </MDTypography>
                          </MDBox>
                        ),
                      },
                      {
                        Header: "Instansi",
                        accessor: "instansi",
                        width: "200px",
                        Cell: ({ value }) => (
                          <MDBox
                            sx={{
                              whiteSpace: "normal",
                              wordWrap: "break-word",
                              width: "200px",
                              py: 1,
                            }}
                          >
                            <MDTypography variant="caption" color="text">
                              {value || "-"}
                            </MDTypography>
                          </MDBox>
                        ),
                      },
                      {
                        Header: "Waktu",
                        accessor: "waktu",
                        width: "20%",
                        Cell: ({ value }) => (
                          <MDTypography variant="caption" color="text">
                            {value ? formatDateTime(value) : "-"}
                          </MDTypography>
                        ),
                      },
                    ],
                    rows: daftarPeserta.map((peserta, index) => ({
                      no: index + 1,
                      nama: peserta.nama_user || peserta.nama,
                      instansi: peserta.instansi,
                      waktu: peserta.waktu,
                      uid_RSS: peserta.uid_RSS,
                      nama_user: peserta.nama_user,
                    })),
                  }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </MDBox>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClosePesertaDialog}
            variant="contained"
            color="primary"
          >
            Tutup
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default EventYangDiikuti;
